// Sign and capture (plan §6 step 5). While the adult signs, face-PRESENT detection watches the
// warm camera and takes the photo by itself once exactly one face is held for 500 ms, with a
// 300 ms white flash as fill light. After 8 s a "Take photo" helper appears, but it only captures
// while exactly one face is in view: a photo is never taken of an empty frame. Only when the
// detector cannot run at all (a broken device) does it capture unchecked, and the server flags
// that pickup (no_face).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Banner, Button } from "@vavr/ui";
import type { StrokeRecorder } from "@vavr/client";
import type { FaceBox } from "@vavr/core";
import type { Camera, Photo } from "../camera.ts";
import type { FaceDetectorLike } from "../face.ts";
import { GATE_HINT, faceRecord, gateStart, gateStep, type GateStatus } from "../face-gate.ts";
import { SignaturePad } from "./SignaturePad.tsx";
import type { FlowState } from "./machine.ts";

export interface Capture { photo: Photo | null; face: FaceBox | null; recorder: StrokeRecorder | null }

export const MANUAL_PHOTO_AFTER_MS = 8000;
const DETECT_EVERY_MS = 150;
const FLASH_MS = 300;

export interface SignStepProps {
  /** The self-service kiosk's round camera mirror, drawn on the pad's corner. */
  mirror?: ReactNode;
  state: FlowState;
  attestation: string;
  camera: Camera | null;
  loadDetector?: () => Promise<FaceDetectorLike | null>;
  capture: Capture;
  onStroke: (enough: boolean) => void;
  onCannotSign: () => void;
  onTypedName: () => void;
  onSkip: () => void;
  onSubmit: () => void;
  /** The face-check hint, shown as the staff strip's camera caption. */
  onFaceHint?: (hint: string) => void;
  busy: boolean;
}

const witnessed = (s: FlowState) => (s.selfService ? "The member of staff who brings your child witnesses it." : "A member of staff has witnessed this handover.");

export function SignStep({ mirror, state, attestation, camera, loadDetector, capture, onStroke, onCannotSign, onTypedName, onSkip, onSubmit, onFaceHint, busy }: SignStepProps) {
  const [photoTaken, setPhotoTaken] = useState(Boolean(capture.photo));
  const [status, setStatus] = useState<GateStatus>("none");
  const [manual, setManual] = useState(false);
  const [detectorOk, setDetectorOk] = useState(true);
  const [noFace, setNoFace] = useState(false);
  const lastGate = useRef(gateStart);
  const [flash, setFlash] = useState(false);
  const [clearKey, setClearKey] = useState(0);
  const taking = useRef(false);
  const hasCamera = Boolean(camera?.video);
  const showPad = state.policy !== "off" && (state.signature === "none" || state.signature === "drawn" || state.signature === "skipped");

  const take = async (face: { d: Parameters<typeof faceRecord>[0] } | null) => {
    if (!camera || taking.current || capture.photo) return;
    taking.current = true;
    setFlash(true);
    // Let the white layer light the face before the frame is read.
    await new Promise((r) => setTimeout(r, 120));
    const photo = await camera.capture();
    setTimeout(() => setFlash(false), FLASH_MS - 120);
    if (photo) {
      capture.photo = photo;
      capture.face = face ? faceRecord(face.d, photo.scale, photo.width, photo.height) : null;
      setPhotoTaken(true);
    }
    taking.current = false;
  };

  // Face-present loop: only while no photo has been taken.
  useEffect(() => {
    if (!camera?.video || capture.photo) return;
    const video = camera.video;
    let live = true;
    let gate = gateStart;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const manualTimer = setTimeout(() => live && setManual(true), MANUAL_PHOTO_AFTER_MS);
    (loadDetector ? loadDetector() : Promise.resolve(null)).then((det) => {
      if (!live) return;
      if (!det) { setDetectorOk(false); setManual(true); return; }
      const tick = () => {
        if (!live || capture.photo) return;
        try {
          gate = gateStep(gate, det.detect(video, performance.now()), video.videoHeight, performance.now());
        } catch { gate = gateStart; }
        lastGate.current = gate;
        setStatus(gate.status);
        if (gate.status !== "none" && gate.status !== "many") setNoFace(false);
        if (gate.status === "ready" && gate.best) { void take({ d: gate.best }); return; }
        timer = setTimeout(tick, DETECT_EVERY_MS);
      };
      tick();
    });
    return () => { live = false; clearTimeout(timer); clearTimeout(manualTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera]);

  // The helper button: capture only while one face is in the frame (any size).
  const takeManual = () => {
    if (!detectorOk) { void take(null); return; }
    const g = lastGate.current;
    if (g.best && g.status !== "many" && g.status !== "none") void take({ d: g.best });
    else setNoFace(true);
  };
  const hint = !hasCamera ? "No camera on this device" : photoTaken ? "Photo taken" : noFace ? "No face found: look at the camera" : GATE_HINT[status];
  const needPhoto = state.direction === "pickup" && state.photoRequired !== false && hasCamera && !photoTaken;
  useEffect(() => { onFaceHint?.(hint); }, [hint, onFaceHint]);
  const doneLabel = state.selfService ? "Send request" : state.direction === "pickup" ? "Finish pickup" : "Finish drop-off";

  return (
    <div className="pmk-sign">
      <p className="pmk-sign__attestation" id="pmk-attestation">{attestation}</p>
      {showPad && (
        <div className="pmk-sign__padwrap">
          <SignaturePad
            label="Signature pad. Sign with your finger. If you cannot, choose I cannot sign or Type my name."
            clearKey={clearKey} disabled={busy}
            onRecorder={(r) => { capture.recorder = r; }}
            onChange={(_, enough) => onStroke(enough)}
          />
          {mirror}
        </div>
      )}
      {state.signature === "cannot_sign" && <Banner tone="info" title="No signature needed">{witnessed(state)}</Banner>}
      {state.signature === "typed_name" && <Banner tone="info" title={`Typed name: ${state.typedName ?? ""}`}>{witnessed(state)}</Banner>}
      {state.policy === "off" && <Banner tone="info" title="No signature needed for drop-off" />}
      {showPad && (
        <div className="pmk-sign__actions">
          <Button variant="secondary" onClick={() => { setClearKey((k) => k + 1); onStroke(false); }} disabled={busy}>Clear</Button>
          {state.policy !== "optional" && <Button variant="ghost" onClick={onCannotSign} disabled={busy}>I cannot sign</Button>}
          <Button variant="ghost" onClick={onTypedName} disabled={busy}>Type my name</Button>
          {state.policy === "optional" && <Button variant="ghost" onClick={onSkip} disabled={busy}>Skip signature</Button>}
        </div>
      )}
      <div className="pmk-sign__actions">
        {hasCamera && manual && !photoTaken && <Button variant="secondary" icon="camera" onClick={takeManual}>Take photo</Button>}
        <Button variant="primary" size="lg" icon="check" onClick={onSubmit} loading={busy} disabled={needPhoto} aria-describedby="pmk-attestation" className="pmk-sign__done">{needPhoto ? "Waiting for your photo" : doneLabel}</Button>
      </div>
      {flash && <div className="pmk-flash" aria-hidden="true" />}
    </div>
  );
}
